import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { hexToRgba } from "@/lib/tags";
import { useTags } from "@/context/TagsContext";
import { cn } from "@/lib/utils";
import NewTagModal from "@/components/tags/NewTagModal";

/**
 * Popover for assigning tags to a debtor. Anchors to whatever element opened
 * it (positioning is caller-provided via absolute positioning on parent).
 *
 * Autosaves 300ms after last toggle (no explicit save button).
 */
const TagAssignPopover = ({ open, debtorId, initialTagIds, anchorRect, onClose, onSaved }) => {
  const { tags, createTag, setDebtorTags } = useTags();
  const [picked, setPicked] = useState(new Set(initialTagIds || []));
  const [search, setSearch] = useState("");
  const [newTagOpen, setNewTagOpen] = useState(false);
  const saveTimer = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (open) setPicked(new Set(initialTagIds || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debtorId]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, onClose]);

  const toggle = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      scheduleSave(next);
      return next;
    });
  };

  const scheduleSave = (nextSet) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await setDebtorTags(debtorId, Array.from(nextSet));
        onSaved?.(Array.from(nextSet));
      } catch (e) {
        toast.error("Couldn't save tags. Please try again.");
      }
    }, 300);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((t) => t.name.toLowerCase().includes(q));
  }, [tags, search]);

  const canCreate = search.trim().length > 0 && !tags.some((t) => t.name.toLowerCase() === search.trim().toLowerCase());

  const style = anchorRect
    ? { position: "fixed", top: Math.min(anchorRect.bottom + 6, window.innerHeight - 380), left: Math.max(8, anchorRect.right - 300) }
    : {};

  const onCreatedFromInline = async (t) => {
    setNewTagOpen(false);
    toggle(t.id);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            ref={rootRef}
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="fixed z-[80] w-[300px] bg-bg-secondary border border-border-default rounded-[10px] shadow-elevated overflow-hidden"
            style={style}
            data-testid="tag-assign-popover"
          >
            <div className="p-3 border-b border-border-default flex items-center gap-2">
              <Search size={14} className="text-text-secondary shrink-0" />
              <input
                autoFocus
                type="text"
                placeholder="Search or create a tag"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 bg-transparent outline-none text-white text-[13px] placeholder:text-text-muted"
                data-testid="tag-assign-search"
              />
              <button type="button" onClick={onClose} className="text-text-secondary hover:text-white p-1">
                <X size={13} />
              </button>
            </div>

            <div className="max-h-[280px] overflow-y-auto">
              {filtered.length === 0 && !canCreate ? (
                <div className="p-4 text-text-secondary text-[13px]">No tags yet.</div>
              ) : (
                filtered.map((t) => {
                  const on = picked.has(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggle(t.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-bg-tertiary text-left"
                      data-testid={`tag-assign-row-${t.slug}`}
                    >
                      <span className="w-4 h-4 rounded-[4px] border flex items-center justify-center"
                            style={{ borderColor: on ? t.color : "#1E3A58", background: on ? t.color : "transparent" }}>
                        {on && <Check size={11} color="#091424" />}
                      </span>
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: t.color }} />
                      <span className="flex-1 text-white text-[13px]">{t.name}</span>
                    </button>
                  );
                })
              )}
              {canCreate && (
                <button
                  type="button"
                  onClick={() => setNewTagOpen(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-accent-pink hover:bg-bg-tertiary border-t border-border-default"
                  data-testid="tag-assign-create"
                >
                  <Plus size={13} />
                  Create tag "{search.trim()}"
                </button>
              )}
            </div>
          </motion.div>

          <NewTagModal
            open={newTagOpen}
            defaultName={search.trim()}
            onClose={() => setNewTagOpen(false)}
            onCreated={onCreatedFromInline}
          />
        </>
      )}
    </AnimatePresence>
  );
};

export default TagAssignPopover;
